import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, Avatar } from 'antd';
import type { MenuProps } from 'antd';
import { LogoutOutlined, UserOutlined, SettingOutlined, ProfileOutlined, CheckOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import SettingsModal from './SettingsModal';
import { db } from '../firebase';
import { collection, getDocs, query, where, doc, setDoc, getDoc } from 'firebase/firestore';
import './Navigation.css';

interface Profile {
  id: string;
  name: string;
  description?: string;
  defaultFilters?: {
    clubs?: string[];
    teams?: string[];
  };
}

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  // Load profiles and selected profile
  useEffect(() => {
    const loadProfiles = async () => {
      if (!user) return;
      
      try {
        const q = query(
          collection(db, 'profiles'),
          where('userId', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Profile));
        setProfiles(data);

        // Load selected profile
        const selectedProfileDoc = await getDoc(doc(db, 'userSettings', user.uid));
        if (selectedProfileDoc.exists()) {
          setSelectedProfileId(selectedProfileDoc.data().selectedProfileId || null);
        }
      } catch (error) {
        console.error('Error loading profiles:', error);
      }
    };

    if (user) {
      loadProfiles();
    }
  }, [user]);

  const handleSelectProfile = async (profileId: string) => {
    if (!user) return;
    
    try {
      await setDoc(doc(db, 'userSettings', user.uid), {
        selectedProfileId: profileId,
        userId: user.uid,
        updatedAt: new Date().toISOString()
      });
      // Reload the entire application to reset all state
      window.location.reload();
    } catch (error) {
      console.error('Error saving selected profile:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const profileMenuItems: MenuProps['items'] = profiles.map(profile => ({
    key: profile.id,
    label: profile.name,
    icon: selectedProfileId === profile.id ? <CheckOutlined /> : null,
    onClick: () => handleSelectProfile(profile.id)
  }));

  const menuItems: MenuProps['items'] = [
    {
      key: 'profiles',
      label: 'Profiles',
      icon: <ProfileOutlined />,
      children: profileMenuItems.length > 0 ? profileMenuItems : [
        {
          key: 'no-profiles',
          label: 'No profiles yet',
          disabled: true
        }
      ]
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingOutlined />,
      onClick: () => setSettingsVisible(true)
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout
    }
  ];

  return (
    <nav className="navigation">
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          Skytedokumenter
        </Link>
        <div className="nav-links">
          <Link to="/" className="nav-link">
            Home
          </Link>
          <Link to="/skyteplott" className="nav-link">
            Skyteplott
          </Link>
          <Link to="/innskytingsskive" className="nav-link">
            Innskytingsskive
          </Link>
          <Link to="/innskytingsliste" className="nav-link">
            Innskytingsliste
          </Link>
          {/* Temporarily hidden - not yet complete
          <Link to="/fordele-skiver" className="nav-link">
            Fordele Skiver
          </Link>
          <Link to="/stafettuttak" className="nav-link">
            Stafettuttak
          </Link>
          */}
          {user && (
            <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={['click']}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                cursor: 'pointer', 
                marginLeft: '20px',
                color: 'white'
              }}>
                <span>
                  {user.displayName || user.email?.split('@')[0]}
                  {selectedProfileId && profiles.length > 0 && (
                    <span style={{ opacity: 0.7, marginLeft: '4px' }}>
                      ({profiles.find(p => p.id === selectedProfileId)?.name})
                    </span>
                  )}
                </span>
                <Avatar 
                  icon={<UserOutlined />} 
                  style={{ 
                    backgroundColor: '#1890ff'
                  }}
                />
              </div>
            </Dropdown>
          )}
        </div>
      </div>
      {user && <SettingsModal visible={settingsVisible} onClose={() => setSettingsVisible(false)} />}
    </nav>
  );
};

export default Navigation;
